<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'FerryRouteCheckInInformation',
  props: {
    shown: {
      required: true,
      type: Boolean,
    },
  },
  computed: {
    ...mapState({
      route: state => state.transportation.routes.route,
      routeCheckInInformations: state => state.transportation.routes.checkInInformations,
      routeCheckInInformationsLoading: state => state.transportation.routes.checkInInformationsLoading,
      saveLoading: state => state.transportation.routes.saveLoading,
      saveError: state => state.transportation.routes.saveError,
    }),
    ...mapGetters({
      minutesSlider: 'transportation/getMinutesSlider',
    }),
    loading() {
      return this.routeCheckInInformationsLoading
          || this.saveLoading;
    },
    changesMade() {
      const changes = this.outbound.address !== this.routeCheckInInformations.outbound.address
        || this.outbound.city !== this.routeCheckInInformations.outbound.city
        || this.outbound.zip !== this.routeCheckInInformations.outbound.zip
        || this.outbound.latestCheckInMinutesBeforeDeparture
          !== this.routeCheckInInformations.outbound.latestCheckInMinutesBeforeDeparture
        || this.inbound.address !== this.routeCheckInInformations.inbound.address
        || this.inbound.city !== this.routeCheckInInformations.inbound.city
        || this.inbound.zip !== this.routeCheckInInformations.inbound.zip
        || this.inbound.latestCheckInMinutesBeforeDeparture
          !== this.routeCheckInInformations.inbound.latestCheckInMinutesBeforeDeparture;
      this.$emit('input', changes);
      return changes;
    },
    allFieldsFilledOut() {
      const allFilledOut = this.outbound.address
          && this.outbound.address.length
          && this.outbound.city.length
          && this.outbound.zip.length
          && this.outbound.latestCheckInMinutesBeforeDeparture
          && this.inbound.address
          && this.inbound.address.length
          && this.inbound.city.length
          && this.inbound.zip.length
          && this.inbound.latestCheckInMinutesBeforeDeparture;
      return !!allFilledOut;
    },
  },
  methods: {
    ...mapActions({
      loadRouteCheckInInformationAction: 'transportation/routeLoadCheckInInformation',
      saveRouteCheckInInformationAction: 'transportation/routeSaveCheckInInformation',
    }),
    loadCheckInInformation() {
      this.loadRouteCheckInInformationAction().then(() => {
        this.outbound = Object.assign({}, this.routeCheckInInformations.outbound);
        this.inbound = Object.assign({}, this.routeCheckInInformations.inbound);
      });
    },
    saveCheckInInformation() {
      this.saveRouteCheckInInformationAction({
        inbound: this.inbound,
        outbound: this.outbound,
      }).then(() => {
        this.$emit('close', true);
      }).catch(() => {});
    },
  },
  data() {
    return {
      outbound: {},
      inbound: {},
    };
  },
  watch: {
    shown() {
      if (this.shown) {
        this.loadCheckInInformation();
        this.$emit('input', this.changesMade);
      }
    },
  },
};
</script>

<template>
  <v-card class="ferry-route-check-in-information-wrapper" v-if="shown">
    <v-card-title>
      <span class="headline">
        {{ $t('transportation.routes.actions.editCheckInInformation.title') }}
        "{{ route.originWebName }} - {{ route.destinationWebName }}"
      </span>
    </v-card-title>
    <v-card-text class="py-0 mb-0">
      <v-progress-linear
        class="my-0"
        v-if="loading"
        :active="loading"
        :indeterminate="true"
        :height="4"
      />
      <v-alert
        class="my-0 py-2"
        outline
        v-if="!saveLoading"
        :value="!!saveError"
        transition="slide-y-transition">
        {{ $t('errors.saveError') }}
      </v-alert>
    </v-card-text>
    <v-card-text class="py-0">
      <v-container grid-list-md>
        <v-layout row justify-space-between>
          <v-flex xs5>
            <h2>
              "{{ route.originWebName }}"
              ({{ $t('transportation.routes.actions.editCheckInInformation.outbound') }})
            </h2>
            <br>
            <v-card-text class="pr-0 pt-0">
              <v-text-field
                v-model="outbound.address"
                :disabled="saveLoading"
                autocomplete="nope"
                counter
                outlined
                maxlength="100"
                :hint="$t('transportation.routes.actions.editCheckInInformation.addressField.hint')"
                :label="$t('transportation.routes.actions.editCheckInInformation.addressField.label')"
                required
              />
            </v-card-text>
            <v-card-text class="pr-0 pt-0">
              <v-layout row justify-space-between>
                <v-flex xs5>
                  <v-text-field
                    v-model="outbound.zip"
                    :disabled="saveLoading"
                    autocomplete="nope"
                    counter
                    outlined
                    maxlength="50"
                    :label="$t('transportation.routes.actions.editCheckInInformation.zipField.label')"
                    required
                  />
                </v-flex>
                <v-flex xs7>
                  <v-text-field
                    v-model="outbound.city"
                    :disabled="saveLoading"
                    autocomplete="nope"
                    counter
                    outlined
                    maxlength="50"
                    :hint="$t('transportation.routes.actions.editCheckInInformation.cityField.hint')"
                    :label="$t('transportation.routes.actions.editCheckInInformation.cityField.label')"
                    required
                  />
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-text class="pr-0 pt-0">
              <br>{{ $t('transportation.routes.actions.editCheckInInformation.latestCheckInTimeLabel') }}
              <v-card-text>
                <v-slider
                  v-model="outbound.latestCheckInMinutesBeforeDeparture"
                  :disabled="saveLoading"
                  thumb-label="always"
                  :label="minutesSlider.label"
                  :color="minutesSlider.color"
                  :min="minutesSlider.min"
                  :max="minutesSlider.max"
                  :thumb-size="minutesSlider.thumbSize"
                  append-icon="alarm"
                ></v-slider>
              </v-card-text>
            </v-card-text>
          </v-flex>
          <v-flex xs2 class="text-xs-center pb-3">
            <v-divider light vertical/>
          </v-flex>
          <v-flex xs5>
            <h2>
              "{{ route.destinationWebName }}"
              ({{ $t('transportation.routes.actions.editCheckInInformation.inbound') }})
            </h2>
            <br>
            <v-card-text class="pr-3 pt-0">
              <v-text-field
                v-model="inbound.address"
                :disabled="saveLoading"
                autocomplete="nope"
                counter
                outlined
                maxlength="100"
                :hint="$t('transportation.routes.actions.editCheckInInformation.addressField.hint')"
                :label="$t('transportation.routes.actions.editCheckInInformation.addressField.label')"
                required
              />
            </v-card-text>
            <v-card-text class="pr-0 pt-0">
              <v-layout row justify-space-between>
                <v-flex xs5>
                  <v-text-field
                    v-model="inbound.zip"
                    :disabled="saveLoading"
                    autocomplete="nope"
                    counter
                    outlined
                    maxlength="50"
                    :label="$t('transportation.routes.actions.editCheckInInformation.zipField.label')"
                    required
                  />
                </v-flex>
                <v-flex xs7>
                  <v-text-field
                    v-model="inbound.city"
                    :disabled="saveLoading"
                    autocomplete="nope"
                    counter
                    outlined
                    maxlength="50"
                    :hint="$t('transportation.routes.actions.editCheckInInformation.cityField.hint')"
                    :label="$t('transportation.routes.actions.editCheckInInformation.cityField.label')"
                    required
                  />
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-text class="pr-0 pt-0">
              <br>{{ $t('transportation.routes.actions.editCheckInInformation.latestCheckInTimeLabel') }}
              <v-card-text>
                <v-slider
                  v-model="inbound.latestCheckInMinutesBeforeDeparture"
                  :disabled="saveLoading"
                  thumb-label="always"
                  :label="minutesSlider.label"
                  :color="minutesSlider.color"
                  :min="minutesSlider.min"
                  :max="minutesSlider.max"
                  :thumb-size="minutesSlider.thumbSize"
                  append-icon="alarm"
                ></v-slider>
              </v-card-text>
            </v-card-text>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        @click="$emit('close')"
        :disabled="loading"
        color="blue darken-1"
        flat>
        {{ $t('inputs.actions.cancel') }}
      </v-btn>
      <v-tooltip :disabled="changesMade" top>
        <template slot="activator" slot-scope="{ on }">
          <div v-on="on">
            <v-btn
              @click="saveCheckInInformation"
              :disabled="loading || !changesMade || !allFieldsFilledOut"
              color="blue darken-1"
              flat>
              {{ $t('inputs.actions.save') }}
            </v-btn>
          </div>
        </template>
        <span>
          {{ $t('inputs.misc.saveDisabled') }}
        </span>
      </v-tooltip>
    </v-card-actions>
  </v-card>
</template>



// WEBPACK FOOTER //
// src/components/transportation/TransportationRouteCheckInInformation.vue